import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import openAVI from '@/assets/openAVI.mp4';

interface LoadingPageProps {
  onLoadingComplete: () => void;
}

export const LoadingPage = ({ onLoadingComplete }: LoadingPageProps) => {
  const [videoError, setVideoError] = useState(false);
  const [videoPlayed, setVideoPlayed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;

    const tryMutedAutoplay = async () => {
      try {
        await video.play();
        setVideoPlayed(true);
      } catch {
        setVideoError(true);
      }
    };

    tryMutedAutoplay();

    const handleVideoEnd = () => {
      onLoadingComplete();
    };

    video.addEventListener('ended', handleVideoEnd);

    return () => {
      video.removeEventListener('ended', handleVideoEnd);
      video.pause();
      video.currentTime = 0;
    };
  }, [onLoadingComplete]);

  const handleClick = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted) {
      video.muted = false;
      video.currentTime = 0;
      try {
        await video.play();
        setVideoError(false);
        setVideoPlayed(true);
      } catch {}
    }
  };

  return (
    <LoadingContainer onClick={handleClick} style={{ cursor: 'pointer', backgroundColor: 'white' }}>
      <VideoElement
        ref={videoRef}
        src={openAVI}
        playsInline
        muted
        autoPlay
      />
      {videoError && !videoPlayed && (
        <VideoHint
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Tap anywhere to play video
        </VideoHint>
      )}
    </LoadingContainer>
  );
};

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  z-index: 9999;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const VideoHint = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  font-size: 0.875rem;
  color: #ffffff;
  text-align: center;
  font-style: italic;
  z-index: 10;
`;
